<template>
  <v-container class="public-book-list py-8">
    <v-card
      v-if="loading"
      class="pa-6 text-center"
      outlined
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-card>

    <v-alert
      v-else-if="notFound"
      type="warning"
      outlined
    >
      {{ $t('bookList.publicListNotFound') }}
    </v-alert>

    <template v-else-if="bookList">
      <!-- Document header -->
      <header class="quote-header mb-8">
        <div class="d-flex flex-column flex-md-row align-md-end">
          <div class="flex-grow-1">
            <div class="text-overline primary--text mb-1">
              {{ $t('bookList.publicBookList') }}
            </div>
            <h1 class="text-h4 text-md-h3 font-weight-bold mb-3 quote-title">
              {{ bookList.name }}
            </h1>
            <div class="d-flex align-center flex-wrap text-caption tertiaryText--text quote-meta">
              <span class="quote-meta-item">
                {{ bookList.bookListItems.length }} {{ $t('bookList.totalTitles').toLowerCase() }}
              </span>
              <span class="quote-meta-dot">·</span>
              <span class="quote-meta-item">
                {{ totalQuantity }} {{ $t('bookList.totalQuantity').toLowerCase() }}
              </span>
              <template v-if="taxRatePct">
                <span class="quote-meta-dot">·</span>
                <span class="quote-meta-item">
                  {{ $t('bookList.vatIncludedNote', { rate: taxRatePct }) }}
                </span>
              </template>
            </div>
          </div>
          <div class="d-flex flex-column flex-sm-row align-sm-center mt-4 mt-md-0 ml-md-6 flex-shrink-0 public-header-actions">
            <share-link-chip :url="publicUrl" />
            <v-btn
              v-if="canShowPublicCart"
              color="primary"
              elevation="0"
              class="mt-3 mt-sm-0 ml-sm-3 public-view-cart"
              @click="openCartDrawer"
            >
              {{ $t('shop.viewCart') }}
              <v-icon class="ml-2">
                $basket
              </v-icon>
            </v-btn>
          </div>
        </div>
        <div class="quote-rule mt-5" />
      </header>

      <v-row>
        <!-- Items: one continuous document -->
        <v-col cols="12">
          <v-card
            outlined
            class="rounded-lg"
          >
            <div
              v-for="(item, index) in sortedItems"
              :key="item.id"
            >
              <v-divider v-if="index > 0" />
              <div class="d-flex pa-4 item-row">
                <div class="cover-wrap flex-shrink-0">
                  <v-img
                    :src="coverUrl(item)"
                    width="64"
                    max-width="64"
                    height="90"
                    contain
                    class="item-cover"
                  />
                  <span
                    v-if="effectiveQuantity(item) > 1"
                    class="qty-badge"
                  >{{ effectiveQuantity(item) }}</span>
                </div>

                <div class="ml-4 flex-grow-1 item-details">
                  <router-link
                    v-if="localBook(item)?.slug"
                    :to="{ name: 'localBookBySlug', params: { slug: localBook(item).slug } }"
                    class="text-subtitle-2 font-weight-bold item-title d-inline-block"
                  >
                    {{ title(item) }}
                  </router-link>
                  <h2
                    v-else
                    class="text-subtitle-2 font-weight-bold item-title"
                  >
                    {{ title(item) }}
                  </h2>
                  <div class="text-caption tertiaryText--text item-caption">
                    <span v-if="localBook(item)?.publisher?.name">{{ localBook(item).publisher.name }}</span>
                    <span v-if="localBook(item)?.isbn"> · ISBN {{ localBook(item).isbn }}</span>
                  </div>
                  <div class="text-caption tertiaryText--text item-caption item-unit mt-1">
                    {{ effectiveQuantity(item) }} × {{ currency(item) }} {{ fmt(unitFinal(item)) }}
                    <span
                      v-if="hasDiscount && lineSavings(item) > 0"
                      class="unit-original ml-1"
                    >{{ currency(item) }} {{ fmt(unitOriginal(item)) }}</span>
                  </div>
                </div>

                <div class="flex-shrink-0 price-block">
                  <div class="price-lines">
                    <div
                      v-if="hasDiscount && lineSavings(item) > 0"
                      class="text-caption price-original"
                    >
                      <span class="d-sr-only">{{ $t('bookList.originalPriceLabel') }}</span>
                      <span class="price-lines__value">{{ currency(item) }} {{ fmt(lineOriginal(item)) }}</span>
                    </div>
                    <div class="text-subtitle-2 font-weight-bold text-no-wrap price-lines__current">
                      <span
                        v-if="hasDiscount && lineSavings(item) > 0"
                        class="d-sr-only"
                      >{{ $t('bookList.discountedPriceLabel') }}</span>
                      <span class="price-lines__value">{{ currency(item) }} {{ fmt(lineFinal(item)) }}</span>
                    </div>
                  </div>
                  <book-list-cart-quantity-control
                    v-if="canShowPublicCart"
                    class="price-block__control"
                    :product-id="item.productId"
                    :book-id="'/books/'+item.productId"
                    :book-list-id="bookList.id"
                    :book="cartBookFromPublicItem(item)"
                    :format="localBook(item)?.format"
                    :initial-quantity="item.quantity || 1"
                    :is-owned="isOwnedPublicItem(item)"
                    :disabled="bookshelfLoading"
                  />
                </div>
              </div>
            </div>
          </v-card>

          <p class="text-caption tertiaryText--text mt-5 mb-0">
            {{ $t('bookList.quotationDisclaimer') }}
          </p>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import gql from 'graphql-tag';
import { mapActions } from 'vuex';
import BookListQueries from '@/graphql/BookListQueries';
import ShareLinkChip from '@/components/ShareLinkChip.vue';
import BookListCartQuantityControl from '@/components/bookList/BookListCartQuantityControl.vue';
import BookshelfMixin from '@/mixins/BookshelfMixin';
import { calculateExportPricing } from '@/utils/bookListCsvExport';
import { canShowPublicBookListCart } from '@/utils/bookListPublicCart';
import { removeIriId } from '@/iris';

export default {
  name: 'PublicBookList',
  components: {
    ShareLinkChip,
    BookListCartQuantityControl,
  },
  mixins: [BookshelfMixin],
  metaInfo() {
    return {
      title: this.bookList?.name || this.$t('routes.publicBookList'),
    };
  },
  data() {
    return {
      bookList: null,
      localBooksMap: {},
      loading: true,
      notFound: false,
    };
  },
  computed: {
    publicUrl() {
      return `${window.location.origin}/q/${this.$route.params.slug}`;
    },
    sortedItems() {
      return [...(this.bookList?.bookListItems || [])].sort((a, b) => {
        if (a.position != null && b.position != null && a.position !== b.position) {
          return a.position - b.position;
        }
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      });
    },
    canShowPublicCart() {
      return canShowPublicBookListCart(this.bookList);
    },
    bookshelfProductIds() {
      return this.sortedItems
        .map((item) => item.productId)
        .filter(Boolean);
    },
    totalQuantity() {
      return this.sortedItems.reduce((total, item) => total + this.effectiveQuantity(item), 0);
    },
    discountPct() {
      return Math.min(Math.max(Number(this.bookList?.discountPercentage || 0), 0), 100);
    },
    hasDiscount() {
      return this.discountPct > 0;
    },
    taxRate() {
      return Math.max(Number(this.bookList?.taxRate?.rate || 0), 0);
    },
    taxRatePct() {
      return this.taxRate > 0 ? Math.round(this.taxRate * 100) : 0;
    },
  },
  watch: {
    '$route.params.slug': function() {
      this.fetchBookList();
    },
  },
  async created() {
    await this.fetchBookList();
  },
  methods: {
    ...mapActions('siteFunctionality', ['changeShowCartDrawer']),
    openCartDrawer() {
      this.changeShowCartDrawer(true);
    },
    async fetchBookList() {
      this.loading = true;
      this.notFound = false;
      this.bookList = null;
      this.localBooksMap = {};
      try {
        let result;
        try {
          result = await this.queryPublicBookList(BookListQueries.getPublicBookListBySlug);
        } catch (error) {
          if (!this.isAllowPublicCartSchemaError(error)) throw error;
          result = await this.queryPublicBookList(BookListQueries.getPublicBookListBySlugLegacy);
        }

        const bookList = this.publicBookListFrom(result);
        this.bookList = bookList;
        this.notFound = !bookList;
        if (bookList?.bookListItems?.length) {
          await this.fetchLocalBooks(bookList.bookListItems.map((item) => item.productId));
        }
      } catch (error) {
        console.error('Error fetching public booklist:', error);
        this.notFound = true;
      } finally {
        this.loading = false;
      }
    },
    queryPublicBookList(query) {
      return this.$apollo.query({
        query,
        variables: {
          slug: this.$route.params.slug,
        },
        fetchPolicy: 'network-only',
      });
    },
    publicBookListFrom(result) {
      const bookList = result.data?.getPublicBySlugBookLists?.[0] || null;
      if (!bookList) return null;

      return {
        ...bookList,
        isPublic: bookList.isPublic !== false,
        allowPublicCart: bookList.allowPublicCart === true,
      };
    },
    isAllowPublicCartSchemaError(error) {
      const messages = [
        error?.message,
        ...(error?.graphQLErrors || []).map((graphQLError) => graphQLError?.message),
      ].filter(Boolean);

      return messages.some((message) => message.includes('allowPublicCart'));
    },
    async fetchLocalBooks(productIds) {
      const uniqueIds = [...new Set(productIds)].filter(Boolean);
      if (!uniqueIds.length) return;

      const aliases = uniqueIds.map((id, i) => {
        return `book${i}: localBook(id: "/local_books/${id}") { id title isbn slug price vatIncluded thumbnailUrl currency { id _id } publisher { id _id name } format { mimeType _id } subjects(first: 20) { edges { node { heading } } } }`;
      }).join('\n');

      try {
        const result = await this.$apollo.query({
          query: gql`query { ${aliases} }`,
          fetchPolicy: 'network-only',
        });
        const map = {};
        Object.values(result.data).forEach((book) => {
          if (book) {
            map[book.id] = book;
          }
        });
        this.localBooksMap = map;
      } catch (error) {
        console.error('Error fetching public book metadata:', error);
      }
    },
    localBook(item) {
      return this.localBooksMap[`/local_books/${item.productId}`] || null;
    },
    title(item) {
      return this.localBook(item)?.title || item.name;
    },
    coverUrl(item) {
      return this.localBook(item)?.thumbnailUrl || item.thumbnailUrl;
    },
    isOwnedPublicItem(item) {
      return !!this.checkInBookshelf(item.productId);
    },
    // Shares the exact pricing rules with the CSV export, so the public page,
    // the export, and checkout can never disagree.
    pricing(item) {
      return calculateExportPricing(item, this.localBook(item), this.taxRate, this.discountPct);
    },
    unitFinal(item) {
      return this.round2(this.pricing(item).finalVatInclusive);
    },
    unitOriginal(item) {
      return this.round2(this.pricing(item).listVatInclusive);
    },
    effectiveQuantity(item) {
      return this.cartQuantityForItem(item) || (item.quantity || 1);
    },
    cartQuantityForItem(item) {
      if (!this.canShowPublicCart) return 0;

      const productId = removeIriId(item.productId);
      const line = this.currentCartOrder?.orderLines?.edges?.find(({ node } = {}) => {
        return node && removeIriId(node.productId) === productId;
      });
      return Number(line?.node?.quantity || 0);
    },
    // Line amounts are rounded to the displayed two decimals for row display.
    lineOriginal(item) {
      return this.round2(this.unitOriginal(item) * this.effectiveQuantity(item));
    },
    lineFinal(item) {
      return this.round2(this.unitFinal(item) * this.effectiveQuantity(item));
    },
    lineSavings(item) {
      return this.round2(this.lineOriginal(item) - this.lineFinal(item));
    },
    round2(value) {
      return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
    },
    fmt(value) {
      return Number(value || 0).toLocaleString('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    currency(item) {
      const currency = this.localBook(item)?.currency || item.baseCurrency;
      if (!currency) return '';
      return currency._id || currency.id?.split('/').pop() || '';
    },
    cartBookFromPublicItem(item) {
      const localBook = this.localBook(item) || {};
      return {
        ...localBook,
        id: `/books/${item.productId}`,
        _id: item.productId,
        title: localBook.title || item.name,
        price: Number(localBook.price ?? item.basePrice ?? 0),
        currency: localBook.currency || item.baseCurrency,
        thumbnailUrl: localBook.thumbnailUrl || item.thumbnailUrl,
        format: localBook.format || null,
        vatIncluded: localBook.vatIncluded ?? item.vatIncluded ?? false,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/quote-document.scss';

// Page-specific dimensions beyond the shared document idiom.
$qty-badge-size: 21px;

.public-book-list {
  max-width: $quote-max-width;
}

/* Document header styles come from the shared partial. */

/* --- Items: one continuous receipt-like document --- */
.cover-wrap {
  position: relative;
}
.item-cover {
  border-radius: $border-radius-root;
  background: $book-grey-lightest;
  box-shadow: 0 1px 4px rgba($book-ink, 0.14);
}
.qty-badge {
  position: absolute;
  top: -($qty-badge-size / 3);
  right: -($qty-badge-size / 2.5);
  min-width: $qty-badge-size;
  height: $qty-badge-size;
  padding: 0 5px;
  border-radius: $qty-badge-size / 2;
  background: var(--v-quaternaryText-base);
  color: var(--v-textOnPrimary-base);
  font-size: 0.6875rem; // matches Vuetify text-overline size
  font-weight: 700;
  line-height: $qty-badge-size;
  text-align: center;
  box-shadow: 0 0 0 2px var(--v-background-base);
}
.item-details {
  min-width: 0;
}
.item-title {
  color: inherit;
  text-decoration: none;
}
a.item-title:hover {
  color: var(--v-primary-base);
  text-decoration: underline;
}
.item-caption {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item-unit .unit-original {
  text-decoration: line-through;
  color: $book-grey-light;
}

/* --- Numbers: tabular, receipt-grade --- */
.price-block {
  width: 160px;
  margin-left: 16px;
  text-align: right;
}
.price-block,
.item-unit {
  font-variant-numeric: tabular-nums;
}
.price-lines,
.price-block__control {
  width: 100%;
}
.price-block__control {
  margin-top: 8px;
  margin-left: 0;
}

@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .price-block__control.booklist-cart-quantity-control--add {
    justify-content: flex-end;
  }

  .price-block__control.booklist-cart-quantity-control--add ::v-deep .booklist-cart-quantity-control__add {
    width: auto;
    min-width: 128px;
    padding: 0 14px !important;
  }
}
.price-original {
  text-decoration: line-through;
  color: $book-grey;
}

// Phone: the price/cart column drops below the cover + details and becomes
// its own row — prices left, cart pill right — instead of squeezing the
// title column.
@media #{map-get($display-breakpoints, 'xs-only')} {
  .item-row {
    flex-wrap: wrap;
  }
  .price-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    margin-top: 12px;
    margin-left: 0;
    text-align: left;
  }
  .price-lines {
    width: auto;
    min-width: 0;
  }
  .price-block__control {
    width: 160px;
    flex: 0 0 160px;
    margin-top: 0;
    margin-left: 0;
  }
  .price-block__control.booklist-cart-quantity-control--add {
    width: 40px;
    flex-basis: 40px;
  }
}
</style>
